import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import useEntryMotion from '../../components/motion/useEntryMotion'
import checkicon from '../../images/checkicon.svg'
import track from '../../lib/track'
import leadAttribution from '../../lib/attribution'
// The report reuses the SEO check's layout and components.
import '../seoCheck/seoCheck.scss'
import './accessibilityCheck.scss'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myzkobag'
const CHECK_COUNT = 18 // keep in step with CHECKS in api/a11y-check.js
const CATS = ['structure', 'media', 'forms', 'navigation']
const STATUS_ORDER = { fail: 0, warn: 1, pass: 2 }

const fill = (text, values = {}) =>
  String(text || '').replace(/\{(\w+)\}/g, (_, key) => (values[key] != null ? values[key] : ''))

/**
 * Free accessibility check.
 *
 * /api/a11y-check reads the page's HTML and runs the WCAG checks that can be
 * judged from markup alone. The report is honest about what it can't see —
 * contrast, keyboard use, screen-reader flow — which is exactly the part of a
 * full audit the lead form offers.
 */
const AccessibilityCheck = () => {
  const { translations: t } = useContext(LanguageContext)
  const animateIn = useEntryMotion()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [url, setUrl] = useState(params.get('url') || '')
  const [state, setState] = useState('idle') // idle | loading | done | error
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const [lead, setLead] = useState({ name: '', email: '' })
  const [leadStatus, setLeadStatus] = useState('idle')
  const resultRef = useRef(null)
  const runId = useRef(0)
  const autoRan = useRef(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const run = async (event) => {
    if (event) event.preventDefault()
    if (!url.trim() || state === 'loading') return
    const id = ++runId.current
    setState('loading')
    setError(null)
    setLeadStatus('idle')
    try {
      const res = await fetch('/api/a11y-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (id !== runId.current) return
      if (!res.ok || !data.checks) {
        setError(data)
        setState('error')
        return
      }
      setResult(data)
      setState('done')
      setParams({ url: url.trim() }, { replace: true })
      track('a11y_check_run', { score: data.score })
      setTimeout(() => resultRef.current && resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
    } catch (err) {
      setError({})
      setState('error')
    }
  }

  // /accessibility-check?url=company.com opens straight into that company's report.
  useEffect(() => {
    if (autoRan.current || !params.get('url')) return
    autoRan.current = true
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkText = (c) => {
    const base = `a11y.${c.id}`
    if (c.status === 'pass') {
      const okKey = c.value.total === 0 && t[`${base}.none`] ? `${base}.none` : `${base}.ok`
      return fill(t[okKey], c.value)
    }
    return fill((c.status === 'fail' && t[`${base}.fail`]) || t[`${base}.bad`], c.value)
  }

  const counts = result
    ? result.checks.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {})
    : {}
  const band = !result ? null : result.score >= 90 && !counts.fail ? 'good' : result.score >= 70 ? 'mid' : 'low'
  const problems = result
    ? result.checks
        .filter((c) => c.status !== 'pass')
        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.weight - a.weight)
    : []

  const submitLead = async (event) => {
    event.preventDefault()
    if (leadStatus === 'sending') return
    setLeadStatus('sending')
    const list = problems.map((c) => `[${c.status.toUpperCase()}] ${t[`a11y.${c.id}.name`]} (WCAG ${c.wcag}): ${checkText(c)}`).join('\n')
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `Accessibility check lead: ${result.finalUrl} (score ${result.score}/100)`,
          name: lead.name,
          email: lead.email,
          website: result.finalUrl,
          score: `${result.score} / 100`,
          message: list || 'No automatic problems found — wants a full manual audit.',
          source: 'Free accessibility check',
          ...leadAttribution(),
        }),
      })
      setLeadStatus(res.ok ? 'sent' : 'error')
      if (res.ok) track('a11y_check_lead', { score: result.score })
    } catch (err) {
      setLeadStatus('error')
    }
  }

  const reset = () => {
    runId.current++
    setState('idle')
    setResult(null)
    setUrl('')
    setParams({}, { replace: true })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const errorText = error ? fill(t[`seocErr.${error.error}`] || t['seocErr.default'], error) : ''
  const radius = 54
  const circumference = 2 * Math.PI * radius

  return (
    <Layout>
      <Seo
        title={t.a11ySeoTitle}
        description={fill(t.a11ySeoDescription, { n: CHECK_COUNT })}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Free accessibility check', path: '/accessibility-check' },
          ]),
        ]}
      />

      <div className='seoc-page'>
        <div className='seoc-content'>
          <p className='seoc-kicker'>{t.a11yKicker}</p>
          <h1 className='seoc-title'>{t.a11yTitle}</h1>
          <p className='seoc-sub'>{fill(t.a11ySub, { n: CHECK_COUNT })}</p>

          <form className='seoc-form' onSubmit={run}>
            <input
              type='text'
              inputMode='url'
              autoComplete='url'
              spellCheck='false'
              aria-label={t.seocPlaceholder}
              placeholder={t.seocPlaceholder}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <button type='submit' disabled={state === 'loading'}>
              {state === 'loading' ? t.seocChecking : t.a11yButton}
            </button>
          </form>

          {state === 'loading' && (
            <div className='seoc-speed-loading a11y-loading' aria-live='polite'>
              <span className='seoc-spinner' aria-hidden='true' />
              <p>{t.a11yLoading}</p>
            </div>
          )}

          {state === 'error' && <p className='seoc-error' role='alert'>{errorText}</p>}

          <AnimatePresence>
            {state === 'done' && result && (
              <motion.div
                ref={resultRef}
                className='seoc-result'
                initial={animateIn ? { opacity: 0, y: 24 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className='seoc-summary'>
                  <div className={`seoc-ring seoc-ring-${band}`}>
                    <svg viewBox='0 0 120 120' aria-hidden='true'>
                      <circle cx='60' cy='60' r={radius} className='seoc-ring-track' />
                      <motion.circle
                        cx='60'
                        cy='60'
                        r={radius}
                        className='seoc-ring-value'
                        strokeDasharray={circumference}
                        initial={animateIn ? { strokeDashoffset: circumference } : false}
                        animate={{ strokeDashoffset: circumference * (1 - result.score / 100) }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </svg>
                    <div className='seoc-ring-label'>
                      <span className='seoc-ring-score'>{result.score}</span>
                      <span className='seoc-ring-caption'>{t.a11yScoreLabel}</span>
                    </div>
                  </div>
                  <div className='seoc-summary-text'>
                    <p className='seoc-url'>{result.finalUrl}</p>
                    <h2>{t[`a11yBand.${band}.title`]}</h2>
                    <p>{t[`a11yBand.${band}.text`]}</p>
                    <div className='seoc-counts'>
                      <span className='seoc-count seoc-count-fail'>{counts.fail || 0} {t.seocFailed}</span>
                      <span className='seoc-count seoc-count-warn'>{counts.warn || 0} {t.seocWarn}</span>
                      <span className='seoc-count seoc-count-pass'>{counts.pass || 0} {t.seocPassed}</span>
                    </div>
                  </div>
                </div>

                {result.facts.limited && <p className='a11y-limited'>{t.a11yLimited}</p>}

                {problems.length > 0 && (
                  <div className='seoc-top'>
                    <h3 className='seoc-block-title'>{t.seocTopFix}</h3>
                    <ol>
                      {problems.slice(0, 3).map((c) => (
                        <li key={c.id} className={`seoc-top-item seoc-top-${c.status}`}>
                          <strong>{t[`a11y.${c.id}.name`]}</strong>
                          <span>{t[`a11y.${c.id}.fix`]}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {CATS.map((cat) => (
                  <section key={cat} className='seoc-cat'>
                    <h3 className='seoc-cat-title'>{t[`a11yCat.${cat}`]}</h3>
                    <ul className='seoc-checks'>
                      {result.checks
                        .filter((c) => c.cat === cat)
                        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
                        .map((c) => (
                          <li key={c.id} className={`seoc-check seoc-check-${c.status}`}>
                            <span className='seoc-check-icon' aria-hidden='true'>
                              {c.status === 'pass' ? '✓' : c.status === 'warn' ? '!' : '✕'}
                            </span>
                            <div>
                              <p className='seoc-check-name'>
                                {t[`a11y.${c.id}.name`]}
                                {c.wcag !== '—' && <span className='a11y-wcag'>WCAG {c.wcag}</span>}
                              </p>
                              <p className='seoc-check-text'>{checkText(c)}</p>
                              {c.status !== 'pass' && (
                                <p className='seoc-check-fix'>
                                  <strong>{t.seocHowFix}</strong> {t[`a11y.${c.id}.fix`]}
                                </p>
                              )}
                            </div>
                          </li>
                        ))}
                    </ul>
                  </section>
                ))}

                {/* What an automatic check can't see */}
                <div className='seoc-panel a11y-manual'>
                  <h3 className='seoc-block-title'>{t.a11yManualTitle}</h3>
                  <ul>
                    {[t.a11yManual1, t.a11yManual2, t.a11yManual3, t.a11yManual4].map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <p className='seoc-small'>{t.a11yManualNote}</p>
                </div>

                <p className='seoc-note'>{t.a11yNote}</p>

                <div className='seoc-lead'>
                  {leadStatus === 'sent' ? (
                    <div className='seoc-sent'>
                      <img src={checkicon} alt='' width='40' height='40' />
                      <div>
                        <h3>{t.seocSentTitle}</h3>
                        <p>{t.a11ySentText}</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={submitLead}>
                      <h3 className='seoc-lead-title'>{t.a11yLeadTitle}</h3>
                      <p className='seoc-lead-sub'>{t.a11yLeadSub}</p>
                      <div className='seoc-lead-row'>
                        <label>
                          <span>{t.quoteName}</span>
                          <input type='text' value={lead.name} autoComplete='name' required onChange={(e) => setLead({ ...lead, name: e.target.value })} />
                        </label>
                        <label>
                          <span>{t.quoteEmail}</span>
                          <input type='email' value={lead.email} autoComplete='email' required onChange={(e) => setLead({ ...lead, email: e.target.value })} />
                        </label>
                      </div>
                      <button type='submit' className='seoc-lead-btn' disabled={leadStatus === 'sending'}>
                        {leadStatus === 'sending' ? t.quoteSending : t.a11yLeadBtn}
                      </button>
                      {leadStatus === 'error' && <p className='seoc-error'>{t['seocErr.default']}</p>}
                      <p className='seoc-lead-note'>{t.quoteNote}</p>
                    </form>
                  )}
                  <div className='seoc-lead-links'>
                    <button type='button' onClick={() => navigate(`/seo-check?url=${encodeURIComponent(result.finalUrl)}`)}>{t.a11ySeoLink} →</button>
                    <button type='button' onClick={reset}>↻ {t.seocAgain}</button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {state !== 'done' && (
            <ul className='seoc-what'>
              {[t.a11yWhat1, t.a11yWhat2, t.a11yWhat3].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default AccessibilityCheck
